<script setup lang="ts">
/**
 * 聊天输入框。
 *
 * 三个必须保住的细节（每一个对应一次真实故障）：
 *
 * 1. **输入法**：`compositionstart/end` 和 `KeyboardEvent.isComposing` 要**同时**判断。
 *    Safari / Firefox 在 `compositionend` 之后还会再发一个 keydown，
 *    只判断其中一个，用户按下"确认候选词"的那一下就会把半句话发出去。
 * 2. **先清空再发送**：`value = ''` 必须在 `onSend()` 之前。反过来的话，
 *    网络慢的时候输入框里还留着上一条，用户会以为没发出去、再按一次回车，
 *    于是同一个问题问两遍。
 * 3. **高度自适应**：`height = 'auto'` 再读到 scrollHeight，顺序不能反。
 *    不先归零的话，删字时高度只增不减（scrollHeight 不会小于当前高度）。
 *    在 Vue 里这段要放在 `flush:'post'` 的 watch 里，否则量到的是改动前的 DOM。
 */

import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { CHAT_MESSAGE_MAX_LENGTH } from '@spirit/contracts';

import { cn } from '../../lib/cn';
import Button from '../ui/Button.vue';

/** 约 8 行之后改为内部滚动，否则输入框会把消息区挤没。 */
const MAX_TEXTAREA_HEIGHT_PX = 200;
/** 剩余不到 20% 才显示字数，避免常态占位分散注意。 */
const COUNTER_VISIBLE_RATIO = 0.8;

const props = withDefaults(
  defineProps<{
    onSend: (text: string) => void | Promise<void>;
    onStop?: (() => void) | undefined;
    sending?: boolean;
    disabled?: boolean;
    disabledReason?: string | null;
    placeholder?: string;
    autoFocus?: boolean;
    className?: string;
  }>(),
  {
    sending: false,
    disabled: false,
    disabledReason: null,
    placeholder: '说说你的困惑…（Enter 发送，Shift+Enter 换行）',
    autoFocus: false,
  },
);

const value = ref('');
const textareaRef = ref<HTMLTextAreaElement | null>(null);
const composing = ref(false);

const tooLong = computed(() => value.value.length > CHAT_MESSAGE_MAX_LENGTH);
const showCounter = computed(() => value.value.length >= CHAT_MESSAGE_MAX_LENGTH * COUNTER_VISIBLE_RATIO);
const canSend = computed(
  () => value.value.trim().length > 0 && !props.sending && !props.disabled && !tooLong.value,
);

function resize(): void {
  const node = textareaRef.value;
  if (node === null) return;
  node.style.height = 'auto';
  node.style.height = `${Math.min(node.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`;
}

// flush:'post' = DOM 更新之后再量高度。用 watchEffect 也能跑，但它在
// 依赖收集阶段就会执行一次，那时 autoFocus 还没生效，量出来的高度是旧的。
watch(value, () => void nextTick(resize), { flush: 'post' });

onMounted(() => {
  resize();
  if (props.autoFocus) textareaRef.value?.focus();
});

function submit(): void {
  const text = value.value.trim();
  if (text.length === 0 || props.sending || props.disabled) return;
  if (text.length > CHAT_MESSAGE_MAX_LENGTH) return;
  value.value = ''; // 先清空，见文件头第 2 条
  void props.onSend(text);
}

function onKeyDown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && props.sending) {
    event.preventDefault();
    props.onStop?.();
    return;
  }
  if (event.key !== 'Enter') return;
  if (event.shiftKey) return;
  if (composing.value || event.isComposing) return;
  event.preventDefault();
  submit();
}
</script>

<template>
  <div :class="cn('border-t border-slate-200 bg-white px-4 py-3', props.className)">
    <div class="mx-auto max-w-3xl">
      <p
        v-if="props.disabled && props.disabledReason !== null"
        class="mb-2 rounded-lg bg-amber-50 px-3 py-1.5 text-xs text-amber-900"
        role="status"
      >
        {{ props.disabledReason }}
      </p>

      <div
        :class="
          cn(
            'flex items-end gap-2 rounded-xl border bg-white p-2 transition-colors',
            tooLong ? 'border-red-400' : 'border-slate-300 focus-within:border-brand-500',
          )
        "
      >
        <textarea
          ref="textareaRef"
          v-model="value"
          rows="1"
          :disabled="props.disabled"
          :placeholder="props.placeholder"
          :aria-invalid="tooLong ? 'true' : undefined"
          aria-label="输入你的问题"
          class="max-h-[200px] min-h-[2.5rem] flex-1 resize-none border-0 bg-transparent px-2 py-2 text-[0.9375rem] leading-relaxed text-slate-900 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed"
          @keydown="onKeyDown"
          @compositionstart="composing = true"
          @compositionend="composing = false"
        />

        <Button
          v-if="props.sending"
          type="button"
          variant="secondary"
          size="md"
          class="shrink-0"
          @click="props.onStop?.()"
        >
          停止生成
        </Button>
        <Button
          v-else
          type="button"
          size="md"
          class="shrink-0"
          :disabled="!canSend"
          aria-label="发送"
          @click="submit"
        >
          发送
        </Button>
      </div>

      <div class="mt-1.5 flex items-center justify-between gap-2 text-[11px] text-slate-400">
        <span>AI 生成内容仅供参考，不构成诊断或治疗建议</span>
        <span v-if="showCounter" :class="cn('tabular-nums', tooLong && 'font-medium text-red-600')">
          {{ value.length }} / {{ CHAT_MESSAGE_MAX_LENGTH }}
        </span>
      </div>
    </div>
  </div>
</template>
